'use client';

import { BUTTON_LABELS } from '@/constants/labels';
import type { IManagedUserSession, IManagedUserSummary } from '@/types';
import { ManagedUserTabTableProps } from './types';
import { useEffect, useMemo } from 'react';
import { useGlobalStore } from '@/store';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { shallow } from 'zustand/shallow';
import {
  useManagedUserCertificateHandler,
  useManagedUserMemoData,
  useManagedUserScorecardHandler,
  useManagedUserSharedProductChangeHandler,
  useManagedUserScoreSidebarCloseHandler,
} from './hooks';

interface UseManagedUserProfileTabsFeatureParams {
  managedUser?: IManagedUserSummary | null;
  managedUserId?: string | null;
  sessionId?: string | null;
}

interface UseManagedUserProfileTabsFeatureResult {
  /** Handler to close the scorecard sidebar and clean up URL query params */
  handleScoreSidebarClose: () => void;
  /** Indicates whether the active user data is currently loading */
  managerActiveUserIsLoading: boolean;
  /** Label for the "Return to Dashboard" button */
  returnToDashboardLabel: string;
  /** Shared props for the managed user tab table components */
  sharedProps: ManagedUserTabTableProps;
  /** Tabs representing different sections of the managed user dashboard */
  tabs: ReturnType<typeof useManagedUserMemoData>['tabs'];
}

/**
 * Feature hook for managed-user profile tabs.
 *
 * Owns dashboard tab orchestration (filtering, options, callbacks, and modal sidebar state)
 * while keeping UI components presentational.
 */
export const useManagedUserProfileTabsFeature = ({
  managedUser,
  managedUserId,
  sessionId,
}: UseManagedUserProfileTabsFeatureParams): UseManagedUserProfileTabsFeatureResult => {
  // ================================
  // Store and router dependencies
  // ================================
  const {
    managerActiveProduct,
    managerActiveUserIsLoading,
    setManagerActiveProduct,
    setManagerActiveUserScorecardIsLoading,
    setSidebarCertificate,
    setSidebarScorecard,
  } = useGlobalStore(
    (state) => ({
      managerActiveProduct: state.managerActiveProduct,
      managerActiveUserIsLoading: state.managerActiveUserIsLoading,
      setManagerActiveProduct: state.setManagerActiveProduct,
      setManagerActiveUserScorecardIsLoading:
        state.setManagerActiveUserScorecardIsLoading,
      setSidebarCertificate: state.setSidebarCertificate,
      setSidebarScorecard: state.setSidebarScorecard,
    }),
    shallow,
  );

  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // ================================
  // Derived memoized state
  // ================================
  const {
    certifications,
    managedUserFullName,
    practicesCertificates,
    productOptions,
    products,
    roleplays,
    tabs,
  } = useManagedUserMemoData({ managedUser, managerActiveProduct });

  
  // ================================
  // Sidebar and action handlers
  // ================================

  // Scorecard handler encapsulates all logic for loading and setting scorecard sidebar data
  // in response to "View Scorecard" actions across the dashboard. It is implemented as a separate hook
  // to keep this complex logic decoupled from the main feature hook and independently testable.
  const { handleViewScoreCardClick } = useManagedUserScorecardHandler({
    managedUser,
    managedUserFullName,
    managedUserId,
    setManagerActiveUserScorecardIsLoading,
    setSidebarScorecard,
  });

  // Certificate handler encapsulates all logic for loading and setting certificate sidebar data
  // in response to "View Certificate" actions across the dashboard. It is implemented as a separate hook
  // to keep this complex logic decoupled from the main feature hook and independently testable.
  const { handleViewCertificateClick } = useManagedUserCertificateHandler({
    managedUserFullName,
    managedUserId,
    products,
    setManagerActiveProduct,
    setSidebarCertificate,
  });

  // Score sidebar close handler encapsulates logic for closing the scorecard sidebar and cleaning up
  // related URL query params. It is implemented as a separate hook to isolate routing concerns from
  // feature orchestration and make the behavior independently testable and reusable.
  const { handleScoreSidebarClose } = useManagedUserScoreSidebarCloseHandler({
    pathname,
    router,
    searchParams,
  });

  // Shared product change handler used by both scorecard and certificate flows to sync active product
  // when a product-specific certificate or scorecard is selected. Implemented as a separate hook to keep
  // this logic decoupled from the main feature hook and independently testable.
  const { handleSharedProductChange } = useManagedUserSharedProductChangeHandler({
    products,
    setManagerActiveProduct,
  });

  // Auto-open scorecard sidebar when `sessionId` exists in query params.
  useEffect(() => {
    if (sessionId) {
      handleViewScoreCardClick({ sessionId } as unknown as IManagedUserSession);
    }
  }, [sessionId, handleViewScoreCardClick]);

  /**
   * Shared props passed to all tab table components to keep them in sync with global dashboard state
   * and prevent prop drilling of individual values and handlers. Tabs receive this as a single prop and
   * can destructure only the fields they need for their own tables/cards.
   */
  const sharedProps: ManagedUserTabTableProps = useMemo(
    () => ({
      activeProduct: managerActiveProduct,
      isLoading: managerActiveUserIsLoading,
      managedUser,
      onProductChange: handleSharedProductChange,
      onViewCertificateClick: handleViewCertificateClick,
      onViewScorecardClick: handleViewScoreCardClick,
      productOptions,
    }),
    [
      handleSharedProductChange,
      handleViewCertificateClick,
      handleViewScoreCardClick,
      managedUser,
      managerActiveProduct,
      managerActiveUserIsLoading,
      productOptions,
    ],
  );

  // ================================
  // Hook API
  // ================================
  return {
    handleScoreSidebarClose,
    managerActiveUserIsLoading,
    returnToDashboardLabel: BUTTON_LABELS.RETURN_TO_TEAM_DASHBOARD,
    sharedProps,
    tabs,
  };
};
